// Skeleton placeholders that mirror real layouts. Use these instead of a
// centered "Loading…" text so cold loads feel instant + structured (UI audit
// Critical #8). Pulse animation is paused when prefers-reduced-motion is on
// (handled globally by the media-query in index.css).

import { type ReactNode } from "react";

function Bar({ className = "" }: { className?: string }) {
  return <div className={`animate-pulse bg-slate-800 rounded ${className}`} aria-hidden="true" />;
}

function Wrap({ children }: { children: ReactNode }) {
  // role=status + aria-live so SR users hear "Loading dashboard" once.
  return (
    <div role="status" aria-live="polite" aria-busy="true" className="space-y-4 pb-4">
      <span className="sr-only">Loading…</span>
      {children}
    </div>
  );
}

/** Today screen skeleton: dashboard ring + macro bars + 4 meal cards + weekly chart strip. */
export function DashboardSkeleton() {
  return (
    <Wrap>
      <div className="mx-4 p-5 rounded-2xl bg-slate-900 border border-slate-800">
        <div className="flex items-center justify-between mb-4">
          <Bar className="h-3 w-20" />
          <Bar className="h-3 w-16" />
        </div>
        {/* Ring placeholder */}
        <div className="flex items-center justify-center w-full mb-3">
          <div className="w-44 h-44 rounded-full border-8 border-slate-800 animate-pulse" aria-hidden="true" />
        </div>
        <div className="flex justify-center mb-5">
          <Bar className="h-7 w-24 rounded-full" />
        </div>
        {/* Macro bars */}
        <div className="space-y-2">
          <Bar className="h-2 w-full" />
          <Bar className="h-2 w-5/6" />
          <Bar className="h-2 w-4/6" />
        </div>
      </div>

      {/* Meal sections */}
      {[1, 2, 3, 4].map((i) => (
        <div key={i} className="mx-4 rounded-2xl bg-slate-900 border border-slate-800 p-4">
          <div className="flex justify-between items-center mb-3">
            <Bar className="h-4 w-24" />
            <Bar className="h-4 w-12" />
          </div>
          <Bar className="h-3 w-3/4" />
        </div>
      ))}
    </Wrap>
  );
}

/** History list skeleton — a few stacked day cards. Title row optional
 * (parent route already renders its own h1, so default is off). */
export function HistorySkeleton({ includeTitle = false }: { includeTitle?: boolean } = {}) {
  return (
    <Wrap>
      {includeTitle && (
        <div className="p-4 pb-0">
          <Bar className="h-7 w-32" />
        </div>
      )}
      {[1, 2, 3].map((i) => (
        <div key={i} className="space-y-2">
          <div className="px-4 py-2 flex items-baseline justify-between">
            <Bar className="h-4 w-28" />
            <Bar className="h-3 w-20" />
          </div>
          <div className="mx-4 p-3 rounded-2xl bg-slate-900 border border-slate-800 space-y-2">
            <Bar className="h-3 w-3/4" />
            <Bar className="h-3 w-1/2" />
          </div>
        </div>
      ))}
    </Wrap>
  );
}

/** App-shell skeleton — used while AuthGate is checking the Supabase session. */
export function AppShellSkeleton() {
  return (
    <div className="min-h-full max-w-md md:max-w-2xl lg:max-w-3xl mx-auto p-4 space-y-4">
      <div className="flex items-center justify-between">
        <Bar className="h-7 w-24" />
        <div className="w-9 h-9 rounded-full bg-slate-800 animate-pulse" aria-hidden="true" />
      </div>
      <DashboardSkeleton />
    </div>
  );
}
